/**
 * PCM helpers for voice-call capture (useCallMode).
 *
 * The backend's audio contract is PCM16 mono @ 24kHz, but microphone capture
 * must run at the DEVICE'S native rate: piping a 48kHz mic MediaStream into
 * an AudioContext forced to 24kHz is a known Chrome/macOS failure mode that
 * yields a perfectly healthy audio graph full of digital zeros (observed
 * live: "MacBook Pro Microphone" track at 48kHz, context at 24kHz, every
 * captured chunk peak=0.000). So the capture context now uses the native
 * rate and we do the 48k→24k (or any-rate→24k) conversion here, explicitly.
 */

/** The sample rate the assistant backend expects for `type: 'audio'` frames. */
export const TARGET_SAMPLE_RATE = 24000;

/**
 * Linear-interpolation resampler. For speech headed to server-side VAD and
 * transcription this is plenty (the common case is exact 2:1 decimation from
 * 48kHz); if we ever need hi-fi conversion, swap in a windowed-sinc kernel
 * behind the same signature.
 */
export function resampleLinear(
  input: Float32Array,
  inRate: number,
  outRate: number
): Float32Array {
  if (inRate === outRate || input.length === 0) return input;
  const outLength = Math.max(1, Math.round((input.length * outRate) / inRate));
  const out = new Float32Array(outLength);
  if (outLength === 1) {
    out[0] = input[0];
    return out;
  }
  const step = (input.length - 1) / (outLength - 1);
  for (let i = 0; i < outLength; i++) {
    const pos = i * step;
    const i0 = Math.floor(pos);
    const i1 = Math.min(i0 + 1, input.length - 1);
    const frac = pos - i0;
    out[i] = input[i0] * (1 - frac) + input[i1] * frac;
  }
  return out;
}

/**
 * Concatenate captured Float32 worklet frames (at `inRate`), resample to
 * TARGET_SAMPLE_RATE, and clamp-convert to Int16 — the exact payload shape
 * `{type: 'audio', data}` sends. Returns a plain number[] because the frame
 * is JSON-serialized.
 */
export function encodePcm16Chunk(
  chunks: readonly Float32Array[],
  inRate: number
): number[] {
  let totalLength = 0;
  for (const chunk of chunks) totalLength += chunk.length;
  const concatenated = new Float32Array(totalLength);
  let offset = 0;
  for (const chunk of chunks) {
    concatenated.set(chunk, offset);
    offset += chunk.length;
  }

  const resampled = resampleLinear(concatenated, inRate, TARGET_SAMPLE_RATE);
  const out = new Array<number>(resampled.length);
  for (let i = 0; i < resampled.length; i++) {
    const s = resampled[i] * 32768;
    out[i] = Math.max(-32768, Math.min(32767, Math.round(s)));
  }
  return out;
}

/** Peak absolute amplitude of the captured frames — silent-capture detector
 *  for the [voice] diagnostics (a live mic delivering zeros is the failure
 *  mode that motivated this module). */
export function peakAmplitude(chunks: readonly Float32Array[]): number {
  let peak = 0;
  for (const chunk of chunks) {
    for (let i = 0; i < chunk.length; i++) {
      const abs = Math.abs(chunk[i]);
      if (abs > peak) peak = abs;
    }
  }
  return peak;
}
